import { forwardRef, useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2 } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { useImpression, useTracker } from '@archipro-website/tracker';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { useUserLoggedIn } from '@modules/user';
import { trackEnquireReveal } from '~/modules/tracking/util/trackEnquireReveal';
import { trackEnquiryEngagement } from '~/modules/tracking/util/trackEnquiryEngagement';
import { trackEnquiryFormEngagement } from '~/modules/tracking/util/trackEnquiryFunnelStage';
import type { EnquiryFunnelStage } from './use-enquiry-funnel-stages';
import {
    BUILDHUB_PROJECTS_API,
    type loader as BuildHubProjectsLoader,
} from '~/routes/remix-api.build-hub.projects';
import {
    useEnquiry,
    type EnquiryProfessional,
    type UseEnquiryProps,
} from '~/modules/enquiry/hooks/use-enquiry';

/**
 * The friendly default message shown in the "Get in touch" enquiry form
 * (BC-4638, Figma 2785:58822). Fixed copy from the design — not the dynamic
 * per-item message used by the standard enquiry form.
 */
const DEFAULT_MESSAGE =
    "Hi there, I saw your work on ArchiPro and I'm interested in working with " +
    'you. Please let me know if you are available for a conversation.';

const formSchema = z.object({
    firstName: z.string().trim().min(1, { message: 'First name is required' }),
    lastName: z.string().trim().min(1, { message: 'Last name is required' }),
    email: z
        .string()
        .trim()
        .min(1, { message: 'Email is required' })
        .email({ message: 'Please enter a valid email' }),
    buildHubId: z.string().optional(),
    message: z.string().trim().min(1, { message: 'Message is required' }),
});

type GetInTouchFormValues = z.infer<typeof formSchema>;

interface GetInTouchEnquiryFormProps {
    /** The professional to submit the enquiry to. */
    professional: EnquiryProfessional;
    /** Kind of page the enquiry is sent from — the enquiry is recorded and
     *  tracked against this, so it must match the host page. */
    itemType: UseEnquiryProps['itemType'];
    /** The item being enquired about. */
    siteTreeID: number;
    /** The item title (used for tracking). */
    itemTitle: string;
    /** Project-only tracking details; ignored for other item types. */
    relatedItem?: UseEnquiryProps['relatedItem'];
    /** Funnel stages already fired this page view — see {@link EnquiryFunnelStage}. */
    firedFunnelStages: Set<EnquiryFunnelStage>;
}

/**
 * Inline "Get in touch" enquiry form for a detail page's accordion. Renders the dark
 * card layout from the design — First/Last name, Email, a "Project brief"
 * dropdown (the user's BuildHubs) and a prefilled Message, with a centred
 * "Send" pill — while delegating submission, the logged-out → login flow, and
 * tracking to the shared {@link useEnquiry} hook.
 */
export const GetInTouchEnquiryForm = ({
    professional,
    itemType,
    siteTreeID,
    itemTitle,
    relatedItem,
    firedFunnelStages,
}: GetInTouchEnquiryFormProps) => {
    const [isSuccess, setIsSuccess] = useState(false);
    const { user, isLoggedIn } = useUserLoggedIn();

    // Enquiry top-of-funnel events (BC-4876), matching the legacy inline form
    // (EnquiryInline / EnquiryForm) so the Snowflake funnel stays continuous:
    // reveal on scroll into view, engagement on first field focus, form
    // engagement on first genuine input — each once per page view, tracked in
    // the caller-owned set so the accordion's remounts don't refire them.
    const tracker = useTracker();
    const formRef = useRef<HTMLFormElement>(null);

    const logFunnelStageOnce = (stage: EnquiryFunnelStage, log: () => void) => {
        if (firedFunnelStages.has(stage)) return;
        firedFunnelStages.add(stage);
        log();
    };

    useImpression({
        ref: formRef,
        onImpression: async () => {
            logFunnelStageOnce('reveal', () => {
                trackEnquireReveal(tracker, {
                    professionalName: professional.Title,
                    itemID: siteTreeID,
                    itemType,
                    source: 'InPage',
                    professionalID: professional.ID,
                    ctaLabel: 'Get in touch',
                });
            });
        },
    });

    const trackFirstFieldFocus = (e: React.FocusEvent<HTMLFormElement>) => {
        if (
            !(
                e.target instanceof HTMLInputElement ||
                e.target instanceof HTMLTextAreaElement ||
                e.target instanceof HTMLSelectElement
            )
        ) {
            return;
        }
        logFunnelStageOnce('engagement', () => {
            trackEnquiryEngagement(tracker, {
                professional: {
                    ID: professional.ID,
                    Title: professional.Title,
                },
                itemID: siteTreeID,
                itemType,
                source: 'InPage',
            });
        });
    };

    const { state, autoSubmitDelay, submitEnquiry } = useEnquiry({
        professional,
        itemType,
        siteTreeID,
        source: 'InPage',
        relatedItem,
        itemTitle,
        ctaLabel: 'Get in touch',
        onSuccess: () => setIsSuccess(true),
    });

    const isSubmitting = state !== 'idle' || autoSubmitDelay;
    const lockEmail = isLoggedIn && !!user.Email;

    const form = useForm<GetInTouchFormValues>({
        resolver: zodResolver(formSchema),
        mode: 'onTouched',
        // Prefill from the logged-in member so they don't retype their details.
        defaultValues: {
            firstName: isLoggedIn ? (user.FirstName ?? '') : '',
            lastName: isLoggedIn ? (user.LastName ?? '') : '',
            email: isLoggedIn ? (user.Email ?? '') : '',
            buildHubId: undefined,
            message: DEFAULT_MESSAGE,
        },
    });

    // Funnel stage: the user started filling the form (once per page view).
    // Watch for genuine input events (type 'change') rather than
    // formState.isDirty — programmatic setValue calls with shouldDirty: true
    // (e.g. the Project brief dropdown) must not count as engagement.
    useEffect(() => {
        const subscription = form.watch((_values, { type }) => {
            if (type !== 'change' || firedFunnelStages.has('formEngagement')) {
                return;
            }
            firedFunnelStages.add('formEngagement');
            trackEnquiryFormEngagement(tracker, {
                professionalName: professional.Title,
                itemID: siteTreeID,
                itemType,
                source: 'InPage',
                professionalID: professional.ID,
            });
        });
        return () => subscription.unsubscribe();
    }, [form, tracker, professional, itemType, siteTreeID, firedFunnelStages]);

    // "Project brief" options — the user's BuildHubs. Fetched straight from the
    // projects route (which only requires login), so the list shows regardless
    // of the buildhub feature flag. Stays optional either way.
    const buildHubFetcher = useFetcher<typeof BuildHubProjectsLoader>();
    // Load once per mount. Guarding on `!data` would loop forever when the
    // route returns null (401/400/500), since data stays falsy and idle.
    const buildHubsRequested = useRef(false);
    useEffect(() => {
        if (isLoggedIn && !buildHubsRequested.current) {
            buildHubsRequested.current = true;
            buildHubFetcher.load(`${BUILDHUB_PROJECTS_API}?limit=100`);
        }
    }, [isLoggedIn, buildHubFetcher]);
    const buildHubs = buildHubFetcher.data?.nodes ?? [];

    const onSubmit = (values: GetInTouchFormValues) => {
        if (isSubmitting) {
            return;
        }
        // The form collects no phone, but the backend requires one to share
        // contact details. Use the member's profile phone when available and
        // only share details when we actually have a phone — otherwise the
        // enquiry still reaches the professional via email/inbox.
        const phoneNumber = isLoggedIn ? (user.Phone ?? '') : '';
        submitEnquiry(
            {
                name: `${values.firstName} ${values.lastName}`.trim(),
                email: values.email,
                phoneNumber,
                shareDetails: phoneNumber ? 'true' : 'false',
                // No postcode is collected in the "Get in touch" form; the
                // shared submit path tolerates an absent postcode.
                postcode: undefined,
                buildHubId: values.buildHubId,
                message: values.message,
            } as unknown as Parameters<typeof submitEnquiry>[0],
            [],
            undefined,
        );
    };

    if (isSuccess) {
        return (
            <div className="flex min-h-[20rem] flex-col items-center justify-center gap-3 text-center text-white">
                <CheckCircle2 className="size-10" />
                <p className="m-0 text-xl font-medium">
                    Thank you for your enquiry!
                </p>
                <p className="m-0 text-base text-white/70">
                    We will get back to you as soon as possible.
                </p>
            </div>
        );
    }

    return (
        <form
            ref={formRef}
            onFocus={trackFirstFieldFocus}
            onSubmit={form.handleSubmit(
                onSubmit,
                // A submit click blocked by validation is a funnel step of its
                // own — without it, users stuck on a failing field are
                // indistinguishable from users who abandoned voluntarily.
                (errors) => {
                    // Field names only — never values. ExtraData carries them
                    // into the warehouse.
                    const fields = Object.keys(errors).join(',');
                    tracker.log('EnquiryValidationError', {
                        url: new URL(window.location.href),
                        data: {
                            Source: 'InPage',
                            fields,
                            ExtraData: JSON.stringify({ fields }),
                        },
                    });
                },
            )}
            className="flex flex-col gap-[1.5rem]"
            noValidate
        >
            <div className="grid grid-cols-2 gap-[0.5rem]">
                <Field
                    label="First name"
                    error={form.formState.errors.firstName?.message}
                >
                    <Input
                        placeholder="e.g. Paul"
                        autoComplete="given-name"
                        disabled={isSubmitting}
                        {...form.register('firstName')}
                    />
                </Field>
                <Field
                    label="Last name"
                    error={form.formState.errors.lastName?.message}
                >
                    <Input
                        placeholder="e.g. Smith"
                        autoComplete="family-name"
                        disabled={isSubmitting}
                        {...form.register('lastName')}
                    />
                </Field>
            </div>

            <Field label="Email" error={form.formState.errors.email?.message}>
                <Input
                    type="email"
                    placeholder="e.g. paul@email.com"
                    autoComplete="email"
                    disabled={isSubmitting || lockEmail}
                    {...form.register('email')}
                />
            </Field>

            <Field label="Project">
                <div className="flex items-center gap-[0.5rem]">
                    <Select
                        // Radix keeps the last value if this goes `undefined`;
                        // feed '' so clearing falls back to the placeholder.
                        value={form.watch('buildHubId') ?? ''}
                        onValueChange={(value) =>
                            form.setValue('buildHubId', value, {
                                shouldDirty: true,
                            })
                        }
                        disabled={isSubmitting}
                    >
                        <SelectTrigger className="h-auto w-full rounded-[0.75rem] border-0 bg-[#fdfdfd] px-[0.875rem] py-[1rem] text-[1.0625rem] text-black data-[placeholder]:text-black/40">
                            <SelectValue placeholder="Select your Project brief" />
                        </SelectTrigger>
                        <SelectContent>
                            {buildHubs.length ? (
                                buildHubs.map((hub) => (
                                    <SelectItem key={hub.ID} value={hub.ID}>
                                        {hub.Title}
                                    </SelectItem>
                                ))
                            ) : (
                                <div className="px-3 py-2 text-sm text-black/50">
                                    {isLoggedIn
                                        ? 'No project briefs yet'
                                        : 'Log in to attach a project brief'}
                                </div>
                            )}
                        </SelectContent>
                    </Select>
                    {form.watch('buildHubId') ? (
                        <button
                            type="button"
                            onClick={() =>
                                form.setValue('buildHubId', undefined, {
                                    shouldDirty: true,
                                })
                            }
                            disabled={isSubmitting}
                            className="shrink-0 rounded-[0.75rem] border border-white/30 px-[0.875rem] py-[1rem] text-[0.9375rem] leading-none text-white/70 transition-colors hover:border-white/50 hover:text-white disabled:opacity-60"
                        >
                            Clear
                        </button>
                    ) : null}
                </div>
            </Field>

            <Field
                label="Message"
                error={form.formState.errors.message?.message}
            >
                <textarea
                    rows={5}
                    placeholder={DEFAULT_MESSAGE}
                    disabled={isSubmitting}
                    className={cn(
                        'w-full resize-none rounded-[0.75rem] bg-[#fdfdfd] px-[0.875rem] py-[1rem]',
                        'text-[1.0625rem] leading-[1.4] text-black placeholder:text-black/40',
                        'ring-offset-0 outline-none focus-visible:ring-2 focus-visible:ring-white/40',
                        'disabled:opacity-60',
                    )}
                    {...form.register('message')}
                />
            </Field>

            <div className="mt-[0.75rem] flex justify-center">
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className={cn(
                        'flex items-center justify-center rounded-full bg-[#181818]',
                        'px-[2.3rem] py-[0.85rem] text-[1.0625rem] font-medium tracking-[0.02em] text-white',
                        'shadow-[0px_4.5px_13.75px_0px_rgba(0,0,0,0.34)]',
                        'transition-opacity hover:opacity-90 disabled:opacity-60',
                    )}
                >
                    {isSubmitting ? 'Sending…' : 'Send'}
                </button>
            </div>
        </form>
    );
};

/** Field wrapper: uppercase white label above the control, error below. */
const Field = ({
    label,
    error,
    children,
}: {
    label: string;
    error?: string;
    children: React.ReactNode;
}) => (
    <label className="flex flex-col gap-[0.5rem]">
        <span className="text-[0.8125rem] leading-[1.1] font-medium tracking-[-0.02em] text-white uppercase">
            {label}
        </span>
        {children}
        {error ? (
            <span className="text-[0.8125rem] leading-[1.2] text-red-300">
                {error}
            </span>
        ) : null}
    </label>
);

/** Off-white rounded input matching the Figma form fields. */
const Input = forwardRef<
    HTMLInputElement,
    React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => (
    <input
        ref={ref}
        className={cn(
            'w-full rounded-[0.75rem] bg-[#fdfdfd] px-[0.875rem] py-[1rem]',
            'text-[1.0625rem] text-black placeholder:text-black/40',
            'ring-offset-0 outline-none focus-visible:ring-2 focus-visible:ring-white/40',
            'disabled:opacity-60',
            className,
        )}
        {...props}
    />
));
Input.displayName = 'GetInTouchInput';
